<template>
  <div class="ai-ppt">
    <TopHeader class="landing-container-top" />
    <Banner />
    <Templates @trail="handleTrail" />
    <AllInOne :handler="handleTrail" />
    <Interact />
    <UserReview @trail="handleTrail" />
    <Faq />
    <StartRightNow :handler="handleTrail" />
    <AppFooter />
  </div>
</template>

<script setup lang="ts">
import AppFooter from '~/design/widgets/bottom-footer/footer-cn.vue';
import StartRightNow from '~/design/widgets/start-right-now/index.vue';
import TopHeader from '~/design/widgets/top-header/index.vue';
import AllInOne from './modules/all-in-one/index.vue';
import Banner from './modules/banner/index.vue';
import Faq from './modules/faq/index.vue';
import Interact from './modules/interact/index.vue';
import Templates from './modules/ppt-templates/index.vue';
import UserReview from './modules/user-review/index.vue';

useHead({
  title: 'AI生成PPT_一键生成PPT+演讲稿_智能PPT创作平台_有言',
  meta: [
    {
      name: 'description',
      content:
        '魔珐有言AI生成PPT工具，输入主题即可同步生成精美幻灯片与专业演讲逐字稿。海量模板、智能排版、多语言支持，并可无缝转换为3D数字人讲解视频与交互智能体。免费试用，立即提升PPT创作效率！',
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'SoftwareApplication',
            name: '魔珐有言 AI生成PPT',
            description:
              '魔珐有言AI生成PPT工具，输入主题即可同步生成精美幻灯片与专业演讲逐字稿。海量模板、智能排版、多语言支持，并可无缝转换为3D数字人讲解视频与交互智能体。',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            brand: {
              '@type': 'Brand',
              name: '魔珐有言',
            },
            url: 'https://www.youyan.com/ai-ppt',
            image: 'https://www.youyan.com/images/ai-ppt-og.jpg',
            offers: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'CNY',
              availability: 'https://schema.org/OnlineOnly',
              description: '免费试用基础功能，付费解锁更多模板和导出权限',
            },
            featureList: [
              'AI一键生成PPT+演讲稿',
              '20000+精选图片和行业模板',
              '智能排版与图文匹配',
              '多语言翻译支持',
              '在线原生编辑',
              '导出PPTX/PDF/图片',
              '无缝对接数字人视频生成',
              '一键转换为交互智能体',
            ],
          },
          {
            '@type': 'FAQPage',
            name: 'AI生成PPT常见问题解答',
            description: '关于魔珐有言AI生成PPT功能的常见问题与解答。',
            mainEntity: [
              {
                '@type': 'Question',
                name: '魔珐有言的AI生成PPT功能是什么？我能免费试用吗？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '魔珐有言的AI生成PPT是一款智能演示文稿创作工具，您只需输入一句话主题、核心关键词或上传完整脚本，AI即可自动完成内容撰写、排版设计和图文搭配，快速生成专业级PPT。同时，AI还会为每一页同步生成逻辑连贯的演讲逐字稿，彻底解决“做PPT”与“写讲稿”分离的痛点。我们提供免费试用，您可以在官网点击“免费试用”按钮，体验基础功能（包括部分模板和生成次数限制）。如需更多高级模板、无水印导出及商用授权，可升级为付费会员。',
                },
              },
              {
                '@type': 'Question',
                name: '如何开始使用AI生成PPT？需要提供哪些信息？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '使用非常简单，三步即可完成：1. 输入内容：在AI生成PPT界面，您可以通过“一句话主题”、“关键词”或“上传脚本”三种方式输入。例如，输入“新能源汽车市场分析”，AI会自动扩展大纲。2. 选择模板：从海量行业模板中挑选您喜欢的风格（科技、教育、营销、政务等）。3. 一键生成：点击生成，AI将在几分钟内完成PPT制作，并同步生成每页的演讲稿。整个过程无需任何设计基础，所有操作均在浏览器内完成。',
                },
              },
              {
                '@type': 'Question',
                name: 'AI生成的PPT质量如何？模板数量多吗？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '魔珐有言内置20000+精选图片和覆盖多行业的专业PPT模板，包括科技、金融、教育、医疗、跨境营销等场景，且模板持续更新。AI基于深度内容理解，能自动生成结构清晰、重点突出的PPT大纲，并智能匹配图文、图标和配色，确保版式美观、逻辑严谨。生成结果可直接用于汇报、教学或后续视频制作，质量媲美专业设计师作品。',
                },
              },
              {
                '@type': 'Question',
                name: '生成的PPT可以编辑吗？支持哪些导出格式？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '当然可以！AI生成的PPT支持在线原生编辑，您可以随时修改文字、图片、背景、颜色，调整页面布局，甚至自定义动画和字体。编辑完成后，支持导出为PPTX、PDF、图片三种格式，方便您用于线下演示、文件分享或进一步加工。所有编辑操作无需安装Office软件，随时随地在线完成。',
                },
              },
              {
                '@type': 'Question',
                name: '有言AI能生成演讲稿吗？讲稿是否可以直接用于视频制作？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '是的，这是魔珐有言的核心优势之一。AI在生成PPT的同时，会为每一页自动撰写逻辑连贯、语气自然的演讲逐字稿。您可以直接使用这份讲稿，也可以在线编辑优化。生成的PPT和讲稿可无缝对接平台的PPT转数字人视频功能：选择数字人形象、场景和语音，即可一键生成3D数字人讲解视频，实现从文案到视频的全流程自动化。',
                },
              },
              {
                '@type': 'Question',
                name: 'AI生成PPT支持多语言吗？能否用于跨境内容创作？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '支持！魔珐有言AI生成PPT内置20+种语言一键翻译功能，您输入的脚本或关键词可以快速翻译为目标语言（如英语、日语、西班牙语等），AI会根据翻译内容生成对应语言的PPT和演讲稿。同时，平台还提供100+种语言TTS语音合成，方便您后续制作多语种数字人视频，是跨境营销和多语言内容创作的绝佳工具。',
                },
              },
              {
                '@type': 'Question',
                name: '我手头有现成的PPT，可以用魔珐有言优化或转视频吗？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '完全可以！您可以直接上传本地PPT文件（PPTX或PDF格式），平台会自动解析内容并生成对应的数字人讲解视频（通过“PPT转视频”功能）。同时，您也可以使用原生PPT编辑工具对上传的PPT进行二次优化，比如调整排版、替换图片、添加动画等，然后再生成视频或智能体。这样不仅能盘活存量文档，还能快速实现内容升级。',
                },
              },
              {
                '@type': 'Question',
                name: 'PPT Speak智能体是什么？它和AI生成PPT有什么关系？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'PPT Speak智能体是魔珐有言2026年推出的知识型数字人交互工具。您可以将AI生成的PPT（或任何PPT）上传，系统会提取全部文字和知识点，构建专属知识库，并生成一个可实时问答的3D数字人。用户观看时，不仅能听数字人按页讲解，还能随时提问，数字人会严格依据PPT内容精准回答。它与AI生成PPT无缝衔接：您用AI生成PPT后，可直接一键转化为智能体，让静态文档变成“7x24小时在线”的交互式知识助手，适用于教学、培训、产品咨询等场景。',
                },
              },
              {
                '@type': 'Question',
                name: '免费版有哪些限制？如何获取更多功能？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '免费版用户可体验AI生成PPT的基础功能，包括：使用部分免费模板，每月有限次数的生成额度，生成的PPT可能带有水印，部分高级功能（如多格式无水印导出、团队协作、专属数字人定制等）需付费解锁。如需无限制使用全部模板、无水印导出、商用授权及更高并发生成能力，您可以升级为企业版或个人专业版。具体价格和套餐可咨询官网客服或点击“立即购买”查看详情。',
                },
              },
              {
                '@type': 'Question',
                name: 'AI生成PPT的速度如何？如果内容较长，有页数限制吗？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'AI生成PPT的速度通常取决于内容长度和复杂度，一般在几分钟内即可完成一份20页左右的演示文稿。平台对单次生成的页数没有严格上限，但建议您根据实际需要合理规划内容结构。如果内容非常长（如超过50页），可以分批次生成或手动调整分页。此外，AI也支持对生成结果进行片段化修改，您可以单独调整某一页内容而无需重新生成全部。',
                },
              },
              {
                '@type': 'Question',
                name: '生成的PPT是否可以多人协作编辑？适合团队使用吗？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '是的，魔珐有言提供团队协作功能，支持创建多个子账号，并分配管理员、创作者、审核员等不同角色。团队成员可以共享PPT项目、数字人资产和视频作品，在线协同编辑和审核内容，所有修改记录均可追溯。这非常适合企业培训部门、市场团队或内容工厂进行标准化、规模化的PPT和视频生产。',
                },
              },
              {
                '@type': 'Question',
                name: '有言AI生成PPT与市面上的其他AIPPT工具有什么不同？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '魔珐有言的核心差异化在于“PPT+逐字稿”同步生成以及与3D数字人视频、智能体的全链路打通。大多数工具仅能生成PPT，而您还需要额外撰写讲稿、录制视频。有言将这三者融为一体：AI生成PPT+讲稿；无缝转视频；一键变智能体。此外，依托魔珐科技自研的3D AIGC技术，数字人形象、语音和动作的真实感远超普通2D工具，真正满足企业级商用需求。',
                },
              },
            ],
          },
        ],
      }),
    },
  ],
});

const handleTrail = () => {
  const userInfo = useUserInfo();
  if (!userInfo.value) {
    navigateTo(
      `/login?redirect=${encodeURIComponent(encodeURIComponent(`${location.origin}/editor/page/ppt`))}`,
    );
  } else {
    location.href = '/editor/page/ppt';
  }
};
</script>

<style scoped></style>
